// Select: the standard selector. `well` is the filled recessed field for forms and toolbars;
// `inline` reads as the value itself until hovered, for property rails. The list opens in the
// kit's MenuPanel, scaling in from the trigger and fading out on close.
// docs/operations/design-standard.md, "Controls".

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconCheck, IconChevronDown } from './icons';
import { MENU_HEADING, MENU_RULE, MenuPanel, followPointer, menuRow, printable, rowsIn, stepRows, useTypeahead } from './MenuPanel';
import { FOCUS, STATE, TOUCH } from './motion';
import { Tag, readable } from './Tag';

export interface SelectOption {
  key: string;
  label: string;
  /** A category colour: the option and the value render as a Tag. */
  hex?: string;
  /** A quieter second line under the label. */
  hint?: string;
  disabled?: boolean;
  /** Options that share a group sit together under its name. Keep a group's options adjacent. */
  group?: string;
}

/** A row under the options that starts something instead of choosing a value ("New type…"). */
export interface SelectAction {
  key: string;
  label: string;
  hint?: string;
  onSelect: () => void;
}

export interface SelectProps {
  /** What is being chosen. The trigger is announced as "label, current value". */
  label: string;
  options: SelectOption[];
  value: string | null;
  onChange: (key: string) => void;
  variant?: 'well' | 'inline';
  size?: 'sm' | 'md';
  /** `fit` sizes the well to its value, for toolbars and filter bars. */
  width?: 'full' | 'fit';
  /** Shown when nothing is chosen. */
  placeholder?: string;
  renderValue?: (option: SelectOption | undefined) => React.ReactNode;
  renderOption?: (option: SelectOption, selected: boolean) => React.ReactNode;
  /** Rows after the options that open a flow: create the missing value in place, hand off. */
  actions?: SelectAction[];
  disabled?: boolean;
  error?: boolean;
  /** Submitted with its form under this name. */
  name?: string;
  /** The form will not submit without a value; trying rings the field and focuses it. */
  required?: boolean;
  autoFocus?: boolean;
  /** Opens on mount: an inline editor whose list is the edit. */
  defaultOpen?: boolean;
  /** The list closed, after a choice or without one. */
  onClose?: () => void;
  /**
   * Focus left the selector and its list. The list sits in a portal, so a wrapper's own onBlur
   * fires the moment focus enters it; an editor that closes on blur listens here instead.
   */
  onBlur?: () => void;
  id?: string;
  'aria-describedby'?: string;
  /** A change is saving: the trigger says so to a screen reader. It stays usable. */
  'aria-busy'?: boolean;
  className?: string;
}

let seq = 0;

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(({
  label,
  options,
  value,
  onChange,
  variant = 'well',
  size = 'md',
  width = 'full',
  placeholder = 'Not set',
  renderValue,
  renderOption,
  actions,
  disabled,
  error,
  name,
  required,
  autoFocus,
  defaultOpen,
  onClose,
  onBlur,
  id,
  'aria-describedby': describedBy,
  'aria-busy': busy,
  className = '',
}, ref) => {
  const [open, setOpen] = useState(() => !!defaultOpen && !disabled);
  const [invalid, setInvalid] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  // Letters typed on the closed trigger, spent once the list has landed.
  const pending = useRef('');
  const typeahead = useTypeahead();
  const [panelId] = useState(() => `ui-select-${++seq}`);

  const setTrigger = useCallback((el: HTMLButtonElement | null) => {
    trigger.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = el;
  }, [ref]);

  const current = options.find((o) => o.key === value);
  useEffect(() => {
    if (current) setInvalid(false);
  }, [current]);
  useEffect(() => {
    if (!open) pending.current = '';
  }, [open]);

  const close = (refocus: boolean, then?: () => void) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
    then?.();
    onClose?.();
  };

  const choose = (o: SelectOption) => {
    if (o.disabled) return;
    close(true, () => { if (o.key !== value) onChange(o.key); });
  };

  const activate = (row: Element | null) => {
    if (!(row instanceof HTMLElement) || !panel.current?.contains(row)) return;
    if (row.dataset.action !== undefined) {
      const act = actions?.[Number(row.dataset.action)];
      if (act) close(true, act.onSelect);
    } else if (row.dataset.index !== undefined) {
      const o = options[Number(row.dataset.index)];
      if (o) choose(o);
    }
  };

  const landed = () => {
    const rows = rowsIn(panel.current);
    let target: HTMLElement | null = null;
    for (let i = 0; i < pending.current.length; i += 1) target = typeahead(pending.current[i], rows);
    pending.current = '';
    (target || rows.find((r) => r.getAttribute('aria-selected') === 'true') || rows[0])?.focus();
  };

  const left = (next: EventTarget | null) =>
    !(next instanceof Node) || (!trigger.current?.contains(next) && !panel.current?.contains(next));

  // Keys the list uses stop here, so a page's own shortcuts (a table's J, K and Enter) never
  // act on them as well.
  const onListKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const rows = rowsIn(panel.current);
    if (stepRows(e, rows)) { e.stopPropagation(); return; }
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        activate(document.activeElement);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close(true);
        break;
      // Focus goes back first so Tab moves on from the trigger, not from body.
      case 'Tab':
        trigger.current?.focus();
        setOpen(false);
        onClose?.();
        break;
      default:
        if (printable(e)) {
          e.preventDefault();
          e.stopPropagation();
          typeahead(e.key, rows)?.focus();
        }
    }
  };

  const onTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (open) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close(true);
      } else if (printable(e)) {
        e.preventDefault();
        e.stopPropagation();
        pending.current += e.key;
      }
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    } else if (printable(e)) {
      e.preventDefault();
      e.stopPropagation();
      pending.current = e.key;
      setOpen(true);
    }
  };

  const showError = error || (invalid && !current);

  // A coloured choice in a well colours the well itself (a tinted fill, the label in its own ink),
  // never a pill nested inside the field: one container per value.
  const tinted = variant === 'well' && !renderValue && !!current?.hex && !disabled;
  const valueNode = renderValue
    ? renderValue(current)
    : current
      ? current.hex && !tinted ? <Tag hex={current.hex}>{current.label}</Tag> : <span className={`block truncate ${tinted ? 'font-semibold' : ''}`}>{current.label}</span>
      : <span className="block truncate text-on-surface-variant">{placeholder}</span>;
  const tintStyle: React.CSSProperties | undefined = tinted && current?.hex
    // A mixed solid colour, not a gradient: the hover state layer is the background-image.
    ? { backgroundColor: `color-mix(in srgb, ${current.hex} 20%, var(--surface-container-high))`, color: readable(current.hex) }
    : undefined;

  const chevron = `flex-none ${tinted ? 'opacity-70' : 'text-on-surface-variant'} transition-transform duration-standard ease-emphasized motion-reduce:transition-none ${open ? 'rotate-180' : ''}`;

  const box = width === 'fit' ? 'inline-flex w-auto max-w-full' : 'flex w-full';
  const triggerClass = variant === 'well'
    ? `${box} min-w-0 items-center justify-between gap-2 rounded-control bg-surface-container-high px-2.5 text-left text-on-surface shadow-recess ${
      size === 'sm' ? 'h-7 text-[13px]' : 'h-8 text-sm'
    } ${showError ? 'ring-2 ring-red-500/60' : ''}`
    : `-mx-1 inline-flex min-h-6 max-w-full items-center gap-1 rounded-inner px-1 text-left text-sm text-on-surface aria-expanded:bg-surface-container-high ${TOUCH}`;

  const optionRow = (o: SelectOption, i: number) => {
    const selected = o.key === value;
    return (
      <div
        key={o.key}
        id={`${panelId}-${i}`}
        role="option"
        aria-selected={selected}
        aria-disabled={o.disabled || undefined}
        tabIndex={-1}
        data-row=""
        data-index={i}
        data-label={o.label}
        onMouseMove={followPointer}
        onClick={() => choose(o)}
        className={menuRow()}
      >
        <span className="min-w-0 flex-1">
          {renderOption
            ? renderOption(o, selected)
            : o.hex ? <Tag hex={o.hex}>{o.label}</Tag> : <span className="block truncate">{o.label}</span>}
          {o.hint && <span className="block truncate text-xs text-on-surface-variant">{o.hint}</span>}
        </span>
        <span className="flex h-4 w-4 flex-none items-center justify-center text-primary">
          {selected && <IconCheck className="h-4 w-4" />}
        </span>
      </div>
    );
  };

  const body: React.ReactNode[] = [];
  let at = 0;
  while (at < options.length) {
    const group = options[at].group;
    if (!group) {
      body.push(optionRow(options[at], at));
      at += 1;
    } else {
      const start = at;
      while (at < options.length && options[at].group === group) at += 1;
      body.push(
        <div key={`group-${start}`} role="group" aria-label={group} className={start > 0 ? 'mt-1' : ''}>
          <div aria-hidden="true" className={MENU_HEADING}>{group}</div>
          {options.slice(start, at).map((o, k) => optionRow(o, start + k))}
        </div>,
      );
    }
  }

  return (
    <>
      <button
        ref={setTrigger}
        id={id}
        type="button"
        disabled={disabled}
        autoFocus={autoFocus}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-invalid={showError || undefined}
        aria-describedby={describedBy}
        aria-busy={busy || undefined}
        aria-label={`${label}, ${current ? current.label : placeholder}`}
        onClick={(e) => {
          // A keyboard-made click (detail 0) after the keydown already opened the list.
          if (open && e.detail === 0) return;
          if (open) close(true); else setOpen(true);
        }}
        onKeyDown={onTriggerKey}
        onKeyUp={(e) => { if (e.key === ' ') e.preventDefault(); }}
        onBlur={(e) => { if (onBlur && left(e.relatedTarget)) onBlur(); }}
        style={tintStyle}
        className={`${triggerClass} ${STATE} transition-[background-color,box-shadow,color,--np-layer] duration-quick ease-emphasized disabled:pointer-events-none disabled:bg-surface-container disabled:text-on-surface-disabled disabled:shadow-none ${FOCUS} ${className}`}
      >
        <span className={width === 'fit' ? 'min-w-0' : 'min-w-0 flex-1'}>{valueNode}</span>
        <IconChevronDown className={`${variant === 'well' ? 'h-4 w-4' : 'h-3.5 w-3.5'} ${chevron}`} />
      </button>
      {(name || required) && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          name={name}
          required={required}
          disabled={disabled}
          value={value ?? ''}
          onChange={() => undefined}
          onInvalid={(e) => {
            // The browser's bubble would point at this hidden input; the kit's red ring goes on
            // the field people can see instead.
            e.preventDefault();
            setInvalid(true);
            trigger.current?.focus();
          }}
          className="sr-only"
        />
      )}
      <MenuPanel
        ref={panel}
        open={open}
        anchor={trigger}
        onDismiss={(refocus) => close(refocus)}
        role="listbox"
        label={label}
        id={panelId}
        matchWidth
        onOpened={landed}
        onKeyDown={onListKey}
        onBlur={(e) => { if (onBlur && left(e.relatedTarget)) onBlur(); }}
      >
        {body}
        {actions && actions.length > 0 && (
          <>
            {options.length > 0 && <div aria-hidden="true" className={MENU_RULE} />}
            {actions.map((a, i) => (
              <div
                key={a.key}
                id={`${panelId}-action-${i}`}
                role="option"
                aria-selected={false}
                tabIndex={-1}
                data-row=""
                data-action={i}
                data-label={a.label}
                onMouseMove={followPointer}
                onClick={() => close(true, a.onSelect)}
                className={menuRow()}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{a.label}</span>
                  {a.hint && <span className="block truncate text-xs text-on-surface-variant">{a.hint}</span>}
                </span>
              </div>
            ))}
          </>
        )}
      </MenuPanel>
    </>
  );
});
Select.displayName = 'Select';

export default Select;
